import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import TrackRow from "@/components/TrackRow";

/**
 * Renders a scrollable list of tracks with play/pause and buy affordances.
 * All row logic lives in <TrackRow /> — this component just orchestrates
 * data, checkout, and the empty state.
 */
export default function TrackList({ tracks, showBuy = true, contextList = null, radioMode = false }) {
  const { current, playing, playTrack, playRadio, toggle } = usePlayer();
  const { user } = useAuth();
  const nav = useNavigate();

  const buy = useCallback(async (track) => {
    if (!user) {
      toast.error("Sign in to buy tracks");
      nav("/login");
      return;
    }
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "track", item_id: track.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  }, [user, nav]);

  if (tracks.length === 0) {
    return (
      <div className="border border-[#2A2A2A]">
        <div className="p-8 text-center font-mono text-xs text-gray-500 uppercase tracking-widest">
          No tracks yet
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
      {tracks.map((t, i) => {
        const isCurrent = current?.id === t.id;
        return (
          <TrackRow
            key={t.id}
            track={t}
            index={i}
            isCurrent={isCurrent}
            isPlaying={playing}
            showBuy={showBuy}
            onPlayToggle={() => {
              if (isCurrent) return toggle();
              const start = radioMode ? playRadio : playTrack;
              start(t, contextList || tracks);
            }}
            onBuy={() => buy(t)}
          />
        );
      })}
    </div>
  );
}
